"use client";

/**
 * How the pill lends its editor to the task pickers (plan ruling P-1: the
 * editor is never swapped on a task's screen). The rich editor is a
 * contenteditable: its anchor is its element, its caret is read through the
 * caret adapter (rich-composer/caret.ts), and because it is UNCONTROLLED a
 * write goes into the editor as well as into the draft.
 *
 * That write is IN PLACE first (editor-write.ts, review-task-11 finding 5): a
 * pick or a prefill replaces only the span that changed, the way typing would,
 * so the caret lands behind the handle and its space, a real list stays a list
 * and undo survives. The editor's own whole write (`setText`: textContent, the
 * caret at the end) is the fallback for a span that will not map, and it is
 * always right about the words. The draft is written FIRST: the in-place write
 * fires the editor's `input`, whose sync then hands the draft the editor's own
 * reading last (Chrome keeps a trailing space as U+00A0), so the words the
 * pickers hold and the caret offsets they read stay one string. What only a
 * real browser proves is listed in editor-write.ts.
 *
 * The port is rebuilt with the words, on purpose: the pickers leaf reads it
 * through a ref it syncs in its FIRST effect, and a child's effects run before
 * its host's, so a port that read the words through a ref written in an effect
 * here would answer the leaf's scan with the LAST commit's words. The two refs
 * the dropdowns hold (the anchor, the textarea) never change identity.
 */
import { useMemo, useRef, type RefObject } from "react";
import type { RichEditorHandle } from "@/components/home-briefing/rich-composer/RichEditor";
import {
  caretRectOf,
  textBeforeCaret,
} from "@/components/home-briefing/rich-composer/caret";
import type { PillEditorPort } from "./PillTaskPickers";
import { writeWordsInPlace } from "./editor-write";

export function usePillEditorPort({
  richRef,
  text,
  setText,
}: {
  richRef: RefObject<RichEditorHandle | null>;
  text: string;
  setText: (next: string) => void;
}): PillEditorPort {
  // The rich editor's element, as a ref the dropdowns can hold. Read through
  // the handle at call time, so it is right from the first commit on.
  const richAnchor = useMemo<RefObject<HTMLElement | null>>(
    () => ({
      get current() {
        return richRef.current?.element() ?? null;
      },
      set current(_el: HTMLElement | null) {
        /* owned by the rich editor */
      },
    }),
    [richRef]
  );
  // Never a textarea on the rich path: the pickers then leave the caret to the
  // editor's own setText, which lands it at the end.
  const noTextarea = useRef<HTMLTextAreaElement | null>(null);

  return useMemo<PillEditorPort>(
    () => ({
      anchorRef: richAnchor,
      textareaRef: noTextarea,
      // Null = the caret is somewhere else: the end of the words.
      caretOffset: () => {
        const el = richRef.current?.element() ?? null;
        const before = el ? textBeforeCaret(el) : null;
        return before === null ? text.length : before.length;
      },
      caretRect: () => {
        const el = richRef.current?.element() ?? null;
        return el ? caretRectOf(el) : null;
      },
      write: (next: string) => {
        setText(next);
        const el = richRef.current?.element() ?? null;
        if (el && writeWordsInPlace(el, next)) return;
        richRef.current?.setText(next);
      },
      focus: () => richRef.current?.focus(),
    }),
    [richAnchor, richRef, text, setText]
  );
}
